import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { neutral, primary, warm } from "style/color";
import { moneyFormat } from "utils/money-format";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import SouthEastIcon from "@mui/icons-material/SouthEast";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { createSearchParams, useNavigate } from "react-router-dom";
import {
  InvestmentsBuyRequestPagePath,
  InvestmentsSellRequestPagePath,
  MyInvestmentsRequestsPage,
} from "constants/pages";
import { USER_ROLE } from "types/account";
import { USER_STATUS } from "types/fund-account-invitation";
import React, { useCallback, useMemo, useState } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { IlliquidInvestmentModal } from "../illiquid-investment-modal";
import InvestmentDetailsModal from "../investment-details-modal";

interface InvestmentRequestAssetCardProps {
  name: string;
  ticker: string;
  marketPrice: number;
  dailyChange: number;
  totalApprovedUnits: number;
  totalPendingUnits: number;
  role: USER_ROLE;
  userStatus: USER_STATUS;
  isAFP: boolean;
  isLiquid: boolean;
  description?: string;
}

// Separate component for daily change display
const DailyChangeDisplay: React.FC<{ dailyChange: number }> = ({
  dailyChange,
}) => (
  <Typography
    variant="bodyCopyRegular"
    sx={{
      color: neutral[600],
      display: "flex",
      alignItems: "center",
    }}
    gutterBottom
  >
    {dailyChange >= 0 ? (
      <>
        <ArrowOutwardIcon fontSize="small" /> +{moneyFormat(dailyChange, 2)}
      </>
    ) : (
      <>
        <SouthEastIcon fontSize="small" /> {moneyFormat(dailyChange, 2)}
      </>
    )}
    %
  </Typography>
);

// Separate component for action buttons
const ActionButtons: React.FC<{
  ticker: string;
  role: USER_ROLE;
  userStatus: USER_STATUS;
  isAFP: boolean;
  isLiquid: boolean;
  onBuyClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onSellClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}> = ({
  ticker,
  role,
  userStatus,
  isAFP,
  isLiquid,
  onBuyClick,
  onSellClick,
}) => {
  const isBuyDisabled =
    role === USER_ROLE.VIEWER || userStatus === USER_STATUS.PENDING || !isAFP;
  const isSellDisabled =
    role === USER_ROLE.VIEWER || userStatus === USER_STATUS.PENDING;

  return (
    <Grid
      item
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: 2.5,
        alignSelf: "center",
        justifyContent: "flex-end",
      }}
      xs={12}
      md={2}
    >
      <IconButton
        sx={{ backgroundColor: primary[500] }}
        disabled={isBuyDisabled}
        onClick={onBuyClick}
        aria-label={`Buy ${ticker}`}
      >
        <AddIcon sx={{ color: "white" }} />
      </IconButton>
      <IconButton
        sx={{ backgroundColor: primary[500] }}
        disabled={isSellDisabled}
        onClick={onSellClick}
        aria-label={`Sell ${ticker}`}
      >
        <RemoveIcon sx={{ color: "white" }} />
      </IconButton>
    </Grid>
  );
};

export const InvestmentRequestAssetCard: React.FC<
  InvestmentRequestAssetCardProps
> = ({
  marketPrice,
  ticker,
  name,
  totalApprovedUnits,
  totalPendingUnits,
  dailyChange,
  userStatus,
  role,
  isAFP,
  isLiquid,
  description,
}) => {
  const navigate = useNavigate();
  const [isIlliquidModalOpen, setIsIlliquidModalOpen] = useState(false);
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);

  // Memoized calculations
  const units = useMemo(
    () => totalApprovedUnits + totalPendingUnits,
    [totalApprovedUnits, totalPendingUnits]
  );
  const totalValue = useMemo(() => units * marketPrice, [units, marketPrice]);

  // Event handlers
  const handleCardClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (ticker) {
        navigate({
          pathname: `${MyInvestmentsRequestsPage}/${ticker}`,
        });
      }
    },
    [ticker, navigate]
  );

  const handleInfoClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDescriptionModalOpen(true);
  }, []);

  const handleBuyClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      if (ticker) {
        navigate({
          pathname: InvestmentsBuyRequestPagePath,
          search: `?${createSearchParams({ ticker })}`,
        });
      }
    },
    [ticker, navigate]
  );

  const handleSellClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();

      if (!ticker) return;

      if (!isLiquid) {
        setIsIlliquidModalOpen(true);
        return;
      }

      navigate({
        pathname: InvestmentsSellRequestPagePath,
        search: `?${createSearchParams({ ticker })}`,
      });
    },
    [ticker, isLiquid, navigate]
  );

  const handleCloseIlliquidModal = useCallback(() => {
    setIsIlliquidModalOpen(false);
  }, []);

  const handleCloseDescriptionModal = useCallback(() => {
    setIsDescriptionModalOpen(false);
  }, []);

  return (
    <>
      <IlliquidInvestmentModal
        open={isIlliquidModalOpen}
        close={handleCloseIlliquidModal}
      />

      <InvestmentDetailsModal
        onClose={handleCloseDescriptionModal}
        isOpen={isDescriptionModalOpen}
        ticker={ticker}
        name={name}
        description={description}
      />

      <Card
        sx={{
          backgroundColor: "white",
          boxShadow: "none",
          display: "flex",
          my: 2,
        }}
      >
        <CardActionArea
          sx={{
            "&:hover": {
              backgroundColor: warm[200],
              cursor: "pointer",
            },
          }}
          onClick={handleCardClick}
        >
          <CardContent sx={{ p: 5, "&:last-child": { pb: 5 }, width: "100%" }}>
            <Grid container alignItems="center">
              <Grid item xs={12} md={2}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="bodyCopyBold"
                    sx={{ color: neutral[900] }}
                  >
                    {name}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                  <InfoOutlinedIcon
                    fontSize="inherit"
                    sx={{
                      cursor: "pointer",
                      flexShrink: 0,
                      alignSelf: "center",
                      mb: 0.3,
                    }}
                    onClick={handleInfoClick}
                    titleAccess="Click to know more"
                    aria-label="Click to know more"
                  />
                  <Typography
                    variant="bodyCopyRegular"
                    sx={{
                      color: neutral[600],
                      display: "inline",
                      mb: 0,
                    }}
                    gutterBottom
                  >
                    {ticker}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={2}>
                <Typography variant="bodyCopyBold" sx={{ color: neutral[900] }}>
                  {moneyFormat(units, 4)}
                </Typography>
              </Grid>

              <Grid
                item
                sx={{
                  display: "flex",
                  flexDirection: "row",
                }}
                xs={12}
                md={2}
              >
                <Box>
                  <Typography
                    variant="bodyCopyBold"
                    sx={{ color: neutral[900] }}
                  >
                    ${moneyFormat(marketPrice)}
                  </Typography>
                  <DailyChangeDisplay dailyChange={dailyChange} />
                </Box>
              </Grid>

              {/* Total Value */}
              <Grid item xs={12} md={2}>
                <Typography
                  variant="subtitle1"
                  component="div"
                  sx={{
                    color:
                      totalPendingUnits !== 0 ? neutral[500] : neutral[900],
                  }}
                >
                  ${moneyFormat(totalValue)}
                </Typography>
              </Grid>

              {/* Action Buttons */}
              <ActionButtons
                ticker={ticker}
                role={role}
                userStatus={userStatus}
                isAFP={isAFP}
                isLiquid={isLiquid}
                onBuyClick={(event: any) => handleBuyClick(event)}
                onSellClick={(event: any) => handleSellClick(event)}
              />
            </Grid>
          </CardContent>
        </CardActionArea>
      </Card>
    </>
  );
};
